import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Lock, ShieldCheck, Crosshair, HeartPulse, Sparkles } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FACTION = {
  Void: "#a78bfa", Bio: "#37e0a0", Plasma: "#00d5ff", Mech: "#4aa3ff",
  Recon: "#38bdf8", Tech: "#22d3ee", Shadow: "#8b5cf6", Cosmic: "#f5c542", Cyber: "#c084fc",
};
const TIER_COLOR = { S: "#f5c542", A: "#c084fc", R: "#4aa3ff" };
const ROLE_ICON = { Tank: ShieldCheck, Support: HeartPulse, DPS: Crosshair };
const img = (p) => (p ? (p.startsWith("http") ? p : BACKEND + p) : null);
const fmt = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

// Mirror of backend SYNERGY_RULES (space_heroes.py) for live client calc.
const R = { pair: 0.10, trio: 0.20, full: 0.25, balanced: 0.10 };
function synergy(placed) {
  const base = placed.reduce((s, h) => s + (h.power_score || 0), 0);
  const n = placed.length;
  const fc = {};
  placed.forEach((h) => { fc[h.faction] = (fc[h.faction] || 0) + 1; });
  const top = n ? Math.max(...Object.values(fc)) : 0;
  const full = n === 5 && top === 5;
  const fb = full ? R.full : top >= 3 ? R.trio : top === 2 ? R.pair : 0;
  const roles = new Set(placed.map((h) => h.archetype || "DPS"));
  const balanced = ["Tank", "Support", "DPS"].every((r) => roles.has(r));
  const cb = balanced ? R.balanced : 0;
  const fname = Object.keys(fc).sort((a, b) => fc[b] - fc[a])[0] || null;
  return { base, fb, cb, total: Math.round(base * (1 + fb + cb)), full, balanced, fname, fcount: top };
}

// Pyramid pad geometry: 2 back (upper), 3 front (lower) — matches the art.
const PAD_POS = [
  { left: "30%", top: "6%" }, { left: "70%", top: "6%" },
  { left: "20%", top: "46%" }, { left: "50%", top: "56%" }, { left: "80%", top: "46%" },
];

// Standalone Squad Deployment / Sentry Duty screen — drop up to 5 heroes onto
// glowing green landing pads, see live Total Power + synergy, then lock it in.
export default function SquadDeploy({ onBack }) {
  const [heroes, setHeroes] = useState(null);
  const [pads, setPads] = useState([null, null, null, null, null]);
  const [drawerPad, setDrawerPad] = useState(-1); // pad index being filled, -1 = closed
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    spaceApi.heroesRoster().then((d) => {
      setHeroes(d.heroes || []);
      const dep = (d.deployed || []).slice(0, 5);
      const next = [null, null, null, null, null];
      dep.forEach((cid, i) => { next[i] = cid; });
      setPads(next);
    }).catch(() => setHeroes([]));
  }, []);

  const byId = useMemo(() => Object.fromEntries((heroes || []).map((h) => [h.card_id, h])), [heroes]);
  const placedHeroes = pads.filter(Boolean).map((c) => byId[c]).filter(Boolean);
  const syn = synergy(placedHeroes);
  const pct = Math.round((syn.fb + syn.cb) * 100);

  const setPad = (i, cid) => setPads((p) => p.map((x, idx) => (idx === i ? cid : x)));
  const clearPad = (i) => setPad(i, null);
  const pickHero = (cid) => {
    if (pads.includes(cid)) { toast.warning("That hero is already on a pad"); return; }
    setPad(drawerPad, cid); setDrawerPad(-1);
  };

  const lock = async () => {
    const ids = pads.filter(Boolean);
    if (!ids.length || busy) { if (!ids.length) toast.warning("Place at least one hero"); return; }
    setBusy(true);
    try {
      const r = await spaceApi.heroesDeploy(ids);
      const t = r?.synergy?.total_power ?? syn.total;
      toast.success(`Squad locked — Total Power ${fmt(t)} ⚡`);
    } catch (e) { toast.error("Could not lock deployment"); }
    finally { setBusy(false); }
  };

  if (heroes === null) {
    return <div className="ss-viewport grid place-items-center" data-testid="squad-deploy-loading"><Loader2 className="animate-spin text-[#39ff7a]" /></div>;
  }

  const available = [...heroes].sort((a, b) => (b.power_score || 0) - (a.power_score || 0));

  return (
    <div className="ss-viewport relative flex flex-col" data-testid="squad-deploy" style={{ background: "radial-gradient(circle at 50% 120%, #06131a, #02060a)" }}>
      <SpaceHomeBtn onClick={onBack} testid="squad-deploy-back" />

      {/* Header + live total power */}
      <div className="shrink-0 px-4 pt-14 pb-2 text-center">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#39ff7a]/80">Sentry Duty · Squad Deployment</p>
        <div className="mt-1 flex items-center justify-center gap-2" data-testid="squad-deploy-total">
          <Sparkles size={16} className={syn.full ? "text-[#f5c542]" : "text-[#39ff7a]"} />
          <span className="font-mono text-3xl font-black tabular-nums" style={{ color: syn.full ? "#f5c542" : "#eafff2", textShadow: syn.full ? "0 0 16px rgba(245,197,66,0.7)" : "0 0 12px rgba(57,255,122,0.5)" }}>{fmt(syn.total)}</span>
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Power</span>
        </div>
        {/* synergy badges */}
        <div className="mt-1.5 flex items-center justify-center gap-1.5 flex-wrap min-h-[20px]">
          {syn.fb > 0 && (
            <span data-testid="squad-deploy-faction-bonus" className="px-2 py-0.5 rounded-full font-mono text-[8px] font-black uppercase tracking-wide border" style={{ borderColor: FACTION[syn.fname] || "#39ff7a", color: FACTION[syn.fname] || "#39ff7a", background: `${(FACTION[syn.fname] || "#39ff7a")}22` }}>
              {syn.full ? `${syn.fname} Full House +25%` : `${syn.fname} ×${syn.fcount} +${Math.round(syn.fb * 100)}%`}
            </span>
          )}
          {syn.balanced && (
            <span data-testid="squad-deploy-balanced-bonus" className="px-2 py-0.5 rounded-full font-mono text-[8px] font-black uppercase tracking-wide border border-[#00d5ff] text-[#00d5ff] bg-[#00d5ff22]">Balanced +10% Crit</span>
          )}
          {pct === 0 && <span className="font-mono text-[8px] uppercase tracking-widest text-white/35">Mix factions & roles for synergy</span>}
        </div>
      </div>

      {/* Landing pad field */}
      <div className="relative flex-1 min-h-0 mx-4 my-2 rounded-xl border border-white/10 overflow-hidden" style={{ background: "linear-gradient(180deg, rgba(57,255,122,0.05), rgba(0,0,0,0.4))" }}>
        {PAD_POS.map((pos, i) => {
          const cid = pads[i]; const h = cid ? byId[cid] : null;
          const fcol = h ? (FACTION[h.faction] || "#39ff7a") : "#39ff7a";
          const Role = h ? (ROLE_ICON[h.archetype] || Crosshair) : null;
          return (
            <button key={i} data-testid={`squad-deploy-pad-${i}`} onClick={() => (h ? clearPad(i) : setDrawerPad(i))}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: pos.left, top: pos.top, width: "27%" }}>
              {/* glowing pad */}
              <div className="relative w-full" style={{ aspectRatio: "1.9/1" }}>
                <div className="absolute inset-0 rounded-[50%]" style={{ border: `2px solid ${h ? fcol : "#39ff7a"}`, boxShadow: `0 0 18px ${h ? fcol : "#39ff7a"}`, background: `${h ? fcol : "#39ff7a"}1f` }} />
              </div>
              {/* hero on the pad */}
              {h ? (
                <div className="-mt-[70%] flex flex-col items-center w-full">
                  <div className="relative w-[70%] rounded-lg overflow-hidden border-2" style={{ borderColor: TIER_COLOR[h.rarity_tier] || "#4aa3ff", boxShadow: `0 0 10px ${fcol}` }}>
                    {img(h.image_path) ? <img src={img(h.image_path)} alt={h.name} className="w-full h-full object-cover" style={{ aspectRatio: "3/4" }} /> : <div className="grid place-items-center bg-black/60 text-white/60" style={{ aspectRatio: "3/4" }}>{h.name?.[0]}</div>}
                    <span className="absolute top-0.5 right-0.5"><X size={11} className="text-white bg-black/60 rounded-full p-[1px]" /></span>
                    {Role && <span className="absolute bottom-0.5 left-0.5"><Role size={11} color={fcol} /></span>}
                  </div>
                  <span className="mt-0.5 font-mono text-[8px] font-black text-white truncate max-w-full">{h.name}</span>
                  <span className="font-mono text-[7px]" style={{ color: fcol }}>{fmt(h.power_score || 0)}</span>
                </div>
              ) : (
                <span className="-mt-[42%] font-mono text-[8px] font-black uppercase tracking-widest text-[#39ff7a]/80">Pad {i + 1}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Lock button */}
      <div className="shrink-0 px-4 pb-4 pt-1">
        <button onClick={lock} disabled={busy} data-testid="squad-deploy-lock"
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-mono text-xs font-black uppercase tracking-widest border-2 border-black text-black shadow-[0_0_20px_rgba(57,255,122,0.4)] active:translate-y-[2px] disabled:opacity-60"
          style={{ background: syn.full ? "#f5c542" : "#39ff7a" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Lock Deployment · {placedHeroes.length}/5
        </button>
      </div>

      {/* Roster drawer */}
      {drawerPad >= 0 && (
        <div className="absolute inset-0 z-30 flex flex-col justify-end bg-black/70" data-testid="squad-deploy-drawer" onClick={() => setDrawerPad(-1)}>
          <div className="bg-[#06131a] border-t-4 border-[#39ff7a] rounded-t-2xl max-h-[62%] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <p className="font-mono text-[11px] font-black uppercase tracking-widest text-white">Assign to Pad {drawerPad + 1}</p>
              <button onClick={() => setDrawerPad(-1)} data-testid="squad-deploy-drawer-close" className="w-8 h-8 grid place-items-center border-2 border-white/20 rounded-full text-white"><X size={15} /></button>
            </div>
            <div className="overflow-y-auto no-scrollbar p-3 grid grid-cols-3 gap-2">
              {available.map((h) => {
                const on = pads.includes(h.card_id);
                const fcol = FACTION[h.faction] || "#4aa3ff";
                const Role = ROLE_ICON[h.archetype] || Crosshair;
                return (
                  <button key={h.card_id} onClick={() => pickHero(h.card_id)} disabled={on} data-testid={`squad-deploy-pick-${h.card_id}`}
                    className={`relative rounded-lg overflow-hidden border-2 text-left ${on ? "opacity-40" : "active:scale-95"}`} style={{ borderColor: TIER_COLOR[h.rarity_tier] || "#4aa3ff" }}>
                    <div style={{ aspectRatio: "3/4" }}>
                      {img(h.image_path) ? <img src={img(h.image_path)} alt={h.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center bg-black/60 text-white/60">{h.name?.[0]}</div>}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-black/75 px-1 py-0.5">
                      <div className="flex items-center gap-1"><Role size={9} color={fcol} /><span className="font-mono text-[8px] font-black text-white truncate">{h.name}</span></div>
                      <div className="font-mono text-[7px]" style={{ color: fcol }}>{h.faction} · {fmt(h.power_score || 0)}</div>
                    </div>
                    {on && <span className="absolute top-1 right-1 font-mono text-[7px] font-black uppercase text-[#39ff7a]">Placed</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
